import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import {
  StationMiniChallengeItemNo,
  StationSuperChallengeItemNo,
  StationType,
} from '../../../../constants/Games/gameStation';
import { getGameTypeDetails } from '../../../../constants/Games/gameType';
import { Icons } from '../../../../constants/icons';
import { SVGIconType } from '../../../../constants/svgIcon';
import {
  QuickCreateGameMode,
  StationDraft,
} from '../../../../models/DraftStore';
import { WebBoosterType } from '../../../../models/UserStore';
import {
  AppBar,
  CreateMemoryGameStationSelectCell,
  FillPNGIcon,
  PngIcon,
  SquareCheckbox,
  SquareCheckboxProps,
  SvgIcon,
  Switch,
} from '../../../Molecules';

import styles from './CreateMemoryGameSelectGamesTemplate.module.css';

type Station = GameTypeGroupAccordionProps['items'];

export type SelectGamesPath = {
  gameType: string;
  stations: Station;
  isSelected: boolean;
  partId: string;
};

type SuperChallengeProps = {
  label?: string | number;
  station: StationSuperChallengeItemNo;
};

type MiniChallengeProps = {
  label?: string | number;
  station: StationMiniChallengeItemNo;
};

type CreateMemoryGameSelectGamesTemplateProps = {
  type: QuickCreateGameMode | undefined;
  webBoosterType: WebBoosterType | undefined;
  isGamifiedTutorials: boolean;
  path: SelectGamesPath[];
  miniChallenges: MiniChallengeProps[];
  superChallenge: SuperChallengeProps | undefined;
  isNextDisabled: boolean;
  expectedPayment: number;
  onSwitchGamifiedTutorial: () => void;
  onClickSelectGameType: (partId: string, checked: boolean) => void;
  onClickSelectGame: (
    partId: string,
    gameIndex: number,
    checked: boolean,
  ) => void;
  onClickBack: () => void;
  onClickNext: () => void;
};

export const CreateMemoryGameSelectGamesTemplate = (
  props: CreateMemoryGameSelectGamesTemplateProps,
) => {
  const {
    // type,
    // webBoosterType,
    isGamifiedTutorials,
    path,
    miniChallenges,
    superChallenge,
    isNextDisabled,
    expectedPayment,
    onSwitchGamifiedTutorial,
    onClickSelectGameType,
    onClickSelectGame,
    onClickBack,
    onClickNext,
  } = props;
  const { t } = useTranslation();

  return (
    <div className={styles.createMemorySelectGames}>
      <AppBar
        title={t('Select Memory Practice')}
        onClickBack={onClickBack}
        trailing={
          <>
            {expectedPayment !== 0 && (
              <div className={styles.paymentContainer}>
                <PngIcon name={'CreationStamina'} width={24} height={24} />
                <div className={styles.payTextContainer}>
                  {t('Pay: ')}
                  <p
                    style={{
                      whiteSpace: 'nowrap',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      marginLeft: '3px',
                      lineHeight: 'normal',
                    }}
                  >
                    {expectedPayment}
                  </p>
                </div>
                {/* {`${t('Pay: ')}${expectedPayment}`} */}
              </div>
            )}
          </>
        }
      />
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          {/* <p className={styles.gameTypeName}>
            {t(getQuickCreateGameModeProps(type).name)}
          </p> */}
          <p className={styles.gameTypeName}>{t('Memory Practice')}</p>
        </div>
        {/* <div className={styles.progress}>
          <BubbleStepsProgressBar currentStep={3} totalSteps={3} />
        </div> */}

        <div className={styles.gamifiedTutorial}>
          <SvgIcon
            name={SVGIconType.TutorGames}
            width={28}
            height={28}
            color={'white'}
          />
          <span>{t('+ Interactive Learning')}</span>
          <div className={styles.switch}>
            <Switch
              checked={isGamifiedTutorials}
              label={isGamifiedTutorials ? 'ON' : 'OFF'}
              onChange={onSwitchGamifiedTutorial}
            />
          </div>
        </div>

        <div className={styles.listContainer}>
          {path.map((item, index) => (
            <GameTypeGroupAccordion
              key={index}
              gameType={item.gameType}
              checked={item.isSelected}
              items={item.stations}
              onChange={(checked) => {
                onClickSelectGameType(item.partId, checked);
              }}
              onClickSelectGame={(gameIndex, checked) => {
                onClickSelectGame(item.partId, gameIndex, checked);
              }}
            />
          ))}
          {miniChallenges.map((miniChallenge, index) => (
            <CreateMemoryGameStationSelectCell
              key={index}
              stationLabel={miniChallenge.label}
              stationItem={miniChallenge.station}
              stationType={StationType.PathChallenge}
              title={''}
              subtitle={''}
              checked
              disabled
            />
          ))}

          {superChallenge ? (
            <CreateMemoryGameStationSelectCell
              stationLabel={superChallenge.label}
              stationItem={superChallenge.station}
              stationType={StationType.SuperChallenge}
              title={''}
              subtitle={''}
              checked
              disabled
            />
          ) : null}
        </div>
      </div>
      <div className={styles.bottomBar}>
        <div className={styles.nextContainer}>
          {/* <button className={styles.nextButton} onClick={onClickBack}>
            <SvgIcon
              className={styles.leftArrowIcon}
              name={SVGIconType.Arrow}
            />
            <span className={styles.nextText}>{t('Back')}</span>
          </button> */}
          <button className={styles.nextButton} onClick={onClickNext}>
            <span className={styles.nextText}>{t('OK')}</span>
            <SvgIcon
              className={styles.rightArrowIcon}
              name={SVGIconType.Arrow}
            />
            {isNextDisabled ? <div className={styles.blackShadow} /> : null}
          </button>
        </div>
      </div>
    </div>
  );
};

type GameTypeGroupAccordionProps = {
  gameType: string;
  items: StationDraft[];
  onClickSelectGame: (gameIndex: number, checked: boolean) => void;
};

const GameTypeGroupAccordion: React.FC<
  GameTypeGroupAccordionProps & SquareCheckboxProps
> = (props) => {
  const { gameType, items, onClickSelectGame, checked, disabled, onChange } =
    props;
  const { t } = useTranslation();
  const title = getGameTypeDetails(gameType).displayName;
  const gameTypeIcon = getGameTypeDetails(gameType).icon;
  const gameTypeSvgIcon = getGameTypeDetails(gameType).svgIcon;

  const [isOpen, setIsOpen] = useState(true);

  const selectedCount = items.filter((item) => item.isSelected).length;
  const totalCount = items.length;

  return (
    <div className={styles.accordion}>
      <div
        className={styles.accordionHeader}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.accordionHeaderBar}>
          {gameTypeSvgIcon ? (
            <SvgIcon
              name={gameTypeSvgIcon}
              height={48}
              width={48}
              color={'#333333'}
              style={{ minWidth: '48px' }}
            />
          ) : gameTypeIcon && Icons[gameTypeIcon] ? (
            <FillPNGIcon src={Icons[gameTypeIcon]} color="#333333" size={48} />
          ) : null}
          <p className={styles.accordionTitle}>{t(title)}</p>
          <span className={styles.accordionItemCount}>
            {selectedCount}/{totalCount}
          </span>
          <motion.div
            className={styles.accordionArrow}
            animate={{ rotate: isOpen ? 90 : 270 }}
            transition={{ duration: 0.3 }}
          >
            <SvgIcon name="Arrow5" color="#808080" width={24} height={24} />
          </motion.div>
        </div>
        <SquareCheckbox
          checked={checked}
          disabled={disabled}
          onChange={onChange}
        />
      </div>

      {isOpen && <div className={styles.accordionGap} />}
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0 }}
        transition={{ duration: 0.6 }}
        className={styles.accordionContent}
      >
        {isOpen &&
          items.map((item, index) => (
            <CreateMemoryGameStationSelectCell
              key={index}
              stationLabel={`${
                item.stationType === StationType.Game ? 'P' : 'L'
              }${item.gameIndex + 1}${item.isForChallenge ? '' : '.1'}`}
              stationItem={item.stationItem}
              stationType={item.stationType}
              title={item.title}
              subtitle={item.subtitle}
              gameType={gameType}
              disabled={item.stationType === StationType.AssistedGame}
              checked={item.isSelected}
              onChange={(selected) => {
                onClickSelectGame(item.gameIndex, selected);
              }}
            />
          ))}
      </motion.div>
    </div>
  );
};
